import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  XMarkIcon,
  UserIcon,
  DocumentTextIcon,
  MapPinIcon,
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import api, { adminApi } from '../../services/api';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { Alert } from '../shared/Alert';
import { ConfirmationModal } from './ConfirmationModal';

interface AdminRequestModalProps {
  requestId: string;
  onClose: () => void;
  onProcessed: () => void;
}

export function AdminRequestModal({ requestId, onClose, onProcessed }: AdminRequestModalProps) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [selfieUrl, setSelfieUrl] = useState<string | null>(null);
  const [idDocUrl, setIdDocUrl] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'payment' | 'approve' | null>(null);

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef<string[]>([]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const { data: detail, isLoading, error } = useQuery({
    queryKey: ['admin-request', requestId],
    queryFn: async () => {
      const response = await adminApi.getRequestDetail(requestId);

      // Fetch document images and create blob URLs
      // Presigned S3 URLs are absolute and must NOT include the JWT Authorization header
      // (S3 rejects requests with extra headers not in the signature)
      // Relative dev URLs go through the axios instance which adds the JWT token
      const fetchImageBlob = async (url: string): Promise<string> => {
        let blob: Blob;
        if (url.startsWith('http')) {
          // Absolute S3 presigned URL — use plain fetch (no auth headers)
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
          blob = await response.blob();
        } else {
          // Relative URL (dev mode) — use axios instance with JWT
          const path = url.startsWith('/api') ? url.slice(4) : url;
          const imageResponse = await api.get(path, { responseType: 'blob' });
          blob = imageResponse.data;
        }
        const blobUrl = URL.createObjectURL(blob);
        blobUrlsRef.current.push(blobUrl);
        return blobUrl;
      };

      if (response.data.selfieDocument) {
        try {
          const selfieResponse = await adminApi.getDocumentUrl(requestId, response.data.selfieDocument.id);
          setSelfieUrl(await fetchImageBlob(selfieResponse.data.url));
        } catch (err) {
          console.error('Failed to load selfie image:', err);
        }
      }
      if (response.data.idDocument) {
        try {
          const idDocResponse = await adminApi.getDocumentUrl(requestId, response.data.idDocument.id);
          setIdDocUrl(await fetchImageBlob(idDocResponse.data.url));
        } catch (err) {
          console.error('Failed to load ID document image:', err);
        }
      }

      return response.data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => adminApi.approve(requestId, adminNotes || undefined),
    onSuccess: () => {
      setConfirmAction(null);
      onProcessed();
    },
    onError: () => {
      setConfirmAction(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => adminApi.reject(requestId, rejectionReason, adminNotes || undefined),
    onSuccess: () => {
      onProcessed();
    },
  });

  const confirmPaymentMutation = useMutation({
    mutationFn: () => adminApi.confirmPayment(requestId, adminNotes || undefined),
    onSuccess: () => {
      setConfirmAction(null);
      onProcessed();
    },
    onError: () => {
      setConfirmAction(null);
    },
  });

  const handleConfirmPayment = () => {
    setConfirmAction('payment');
  };

  const handleApprove = () => {
    setConfirmAction('approve');
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      return;
    }
    rejectMutation.mutate();
  };

  const isReadOnly = detail?.status === 'Completed' || detail?.status === 'Rejected';

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Review Verification Request
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 max-h-[70vh] overflow-y-auto">
                    {isLoading ? (
                      <LoadingSpinner size="lg" className="py-8" />
                    ) : error ? (
                      <Alert type="error" message="Failed to load request details" />
                    ) : detail ? (
                      <div className="space-y-6">
                        {/* Status Badge for completed/rejected */}
                        {isReadOnly && (
                          <div className={`flex items-center space-x-2 px-4 py-3 rounded-lg ${
                            detail.status === 'Completed'
                              ? 'bg-green-50 border border-green-200'
                              : 'bg-red-50 border border-red-200'
                          }`}>
                            {detail.status === 'Completed' ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-600" />
                            )}
                            <span className={`text-sm font-medium ${
                              detail.status === 'Completed' ? 'text-green-800' : 'text-red-800'
                            }`}>
                              This request has been {detail.status === 'Completed' ? 'completed' : 'rejected'}
                              {detail.status === 'Rejected' && detail.rejectionReason && (
                                <> &mdash; {detail.rejectionReason}</>
                              )}
                            </span>
                          </div>
                        )}

                        {/* Error Messages */}
                        {approveMutation.isError && (
                          <Alert
                            type="error"
                            message={(approveMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to approve request'}
                          />
                        )}
                        {rejectMutation.isError && (
                          <Alert
                            type="error"
                            message={(rejectMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to reject request'}
                          />
                        )}
                        {confirmPaymentMutation.isError && (
                          <Alert
                            type="error"
                            message={(confirmPaymentMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to confirm payment'}
                          />
                        )}

                        {/* Documents Side by Side */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Selfie */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Selfie</h3>
                            {selfieUrl ? (
                              <img
                                src={selfieUrl}
                                alt="Selfie"
                                className="w-full h-64 object-cover rounded-lg border border-gray-200"
                              />
                            ) : (
                              <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                                <p className="text-gray-500">No selfie</p>
                              </div>
                            )}
                          </div>

                          {/* ID Document */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">ID Document</h3>
                            {idDocUrl ? (
                              <img
                                src={idDocUrl}
                                alt="ID Document"
                                className="w-full h-64 object-cover rounded-lg border border-gray-200"
                              />
                            ) : (
                              <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                                <p className="text-gray-500">No ID document</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Personal Info */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <UserIcon className="h-5 w-5 text-gray-500" />
                            <h3 className="font-medium text-gray-900">Personal Information</h3>
                          </div>
                          <dl className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <dt className="text-gray-500">Full Name</dt>
                              <dd className="font-medium text-gray-900">
                                {detail.firstName} {detail.lastName}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">ID Number</dt>
                              <dd className="font-medium text-gray-900 font-mono">{detail.idNumber}</dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">Date of Birth</dt>
                              <dd className="font-medium text-gray-900">
                                {new Date(detail.dateOfBirth).toLocaleDateString()}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">Email</dt>
                              <dd className="font-medium text-gray-900">{detail.user.email}</dd>
                            </div>
                          </dl>
                        </div>

                        {/* Address */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <MapPinIcon className="h-5 w-5 text-gray-500" />
                            <h3 className="font-medium text-gray-900">Address</h3>
                          </div>
                          <p className="text-sm text-gray-900">
                            {detail.streetAddress}
                            <br />
                            {detail.city}, {detail.province} {detail.postalCode}
                          </p>
                          {detail.geolocation && (
                            <p className="text-xs text-gray-500 mt-2">
                              Location: {detail.geolocation.city}, {detail.geolocation.country} |
                              IP: {detail.geolocation.ipAddress}
                            </p>
                          )}
                        </div>

                        {/* Payment Info */}
                        {detail.payment && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-3">
                              <CreditCardIcon className="h-5 w-5 text-gray-500" />
                              <h3 className="font-medium text-gray-900">Payment</h3>
                            </div>
                            <dl className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <dt className="text-gray-500">Amount</dt>
                                <dd className="font-medium text-gray-900">
                                  R{detail.payment.amount.toFixed(2)}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Method</dt>
                                <dd className="font-medium text-gray-900">{detail.payment.paymentMethod}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Status</dt>
                                <dd className={`font-medium ${
                                  detail.payment.status === 'Completed' ? 'text-green-600' : 'text-yellow-600'
                                }`}>
                                  {detail.payment.status}
                                </dd>
                              </div>
                              {detail.payment.completedAt && (
                                <div>
                                  <dt className="text-gray-500">Paid At</dt>
                                  <dd className="font-medium text-gray-900">
                                    {new Date(detail.payment.completedAt).toLocaleString()}
                                  </dd>
                                </div>
                              )}
                            </dl>
                            {detail.payment.status === 'Pending' && detail.payment.paymentMethod === 'EFT' && (
                              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-yellow-800">EFT Payment Pending Confirmation</p>
                                <p className="text-xs text-yellow-700 mt-1">
                                  Verify that payment has been received in the bank account before confirming.
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Consent Info */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                            <h3 className="font-medium text-gray-900">Consent</h3>
                          </div>
                          <dl className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <dt className="text-gray-500">Consent Given</dt>
                              <dd className="font-medium text-gray-900">
                                {detail.consentGivenAt
                                  ? new Date(detail.consentGivenAt).toLocaleString()
                                  : 'Not given'}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">IP Address</dt>
                              <dd className="font-medium text-gray-900">{detail.consentIpAddress || 'N/A'}</dd>
                            </div>
                          </dl>
                        </div>

                        {/* Admin Notes */}
                        {!isReadOnly && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Admin Notes (Optional)
                            </label>
                            <textarea
                              value={adminNotes}
                              onChange={(e) => setAdminNotes(e.target.value)}
                              rows={2}
                              className="input"
                              placeholder="Internal notes..."
                            />
                          </div>
                        )}

                        {/* Show existing admin notes for read-only */}
                        {isReadOnly && detail.adminNotes && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Admin Notes</h3>
                            <p className="text-sm text-gray-600 whitespace-pre-line">{detail.adminNotes}</p>
                          </div>
                        )}

                        {/* Rejection Form */}
                        {!isReadOnly && showRejectForm && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <label className="block text-sm font-medium text-red-700 mb-2">
                              Rejection Reason *
                            </label>
                            <textarea
                              value={rejectionReason}
                              onChange={(e) => setRejectionReason(e.target.value)}
                              rows={3}
                              className="input"
                              placeholder="Please provide a reason for rejection..."
                              required
                            />
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end space-x-4 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    {isReadOnly ? (
                      <button onClick={onClose} className="btn-secondary">
                        Close
                      </button>
                    ) : detail?.status === 'PendingPayment' && detail?.payment?.status === 'Pending' && detail?.payment?.paymentMethod === 'EFT' ? (
                      <>
                        <button onClick={onClose} className="btn-secondary">
                          Close
                        </button>
                        <button
                          onClick={handleConfirmPayment}
                          disabled={confirmPaymentMutation.isPending}
                          className="btn-primary flex items-center"
                        >
                          {confirmPaymentMutation.isPending ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <>
                              <BanknotesIcon className="h-5 w-5 mr-2" />
                              Confirm EFT Payment
                            </>
                          )}
                        </button>
                      </>
                    ) : !showRejectForm ? (
                      <>
                        <button onClick={onClose} className="btn-secondary">
                          Cancel
                        </button>
                        <button
                          onClick={() => setShowRejectForm(true)}
                          className="btn-danger flex items-center"
                        >
                          <XCircleIcon className="h-5 w-5 mr-2" />
                          Reject
                        </button>
                        <button
                          onClick={handleApprove}
                          disabled={approveMutation.isPending || detail?.payment?.status !== 'Completed'}
                          className="btn-primary flex items-center"
                        >
                          {approveMutation.isPending ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <>
                              <CheckCircleIcon className="h-5 w-5 mr-2" />
                              Approve & Query DHA
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setShowRejectForm(false)} className="btn-secondary">
                          Cancel
                        </button>
                        <button
                          onClick={handleReject}
                          disabled={rejectMutation.isPending || !rejectionReason.trim()}
                          className="btn-danger flex items-center"
                        >
                          {rejectMutation.isPending ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <>
                              <XCircleIcon className="h-5 w-5 mr-2" />
                              Confirm Rejection
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Confirm EFT Payment Modal — rendered INSIDE Dialog.Panel to stay within focus trap */}
                  <ConfirmationModal
                    isOpen={confirmAction === 'payment'}
                    onClose={() => setConfirmAction(null)}
                    onConfirm={() => confirmPaymentMutation.mutate()}
                    title="Confirm EFT Payment"
                    message="Are you sure you want to confirm this EFT payment as received? Please ensure the payment has been verified in the bank account. This action cannot be undone."
                    confirmLabel="Confirm Payment"
                    confirmColor="yellow"
                    icon="warning"
                    isLoading={confirmPaymentMutation.isPending}
                  />

                  {/* Approve & Query DHA Modal — rendered INSIDE Dialog.Panel to stay within focus trap */}
                  <ConfirmationModal
                    isOpen={confirmAction === 'approve'}
                    onClose={() => setConfirmAction(null)}
                    onConfirm={() => approveMutation.mutate()}
                    title="Approve & Query DHA"
                    message="Are you sure you want to approve this request? This will query the Department of Home Affairs database via Ndende to verify the applicant's marital status."
                    confirmLabel="Approve & Query DHA"
                    confirmColor="green"
                    icon="shield"
                    isLoading={approveMutation.isPending}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
  );
}
